import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { LifeSpaceColorIcon } from '@/src/renderer/components/icons/LifeSpaceColorIcon'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/src/renderer/components/ui/dropdown-menu'
import { WaveProgress } from '@/src/renderer/components/ui/WaveProgress'
import { useProjectTaskProgress } from '../../hooks/useProjectTaskProgress'
import { ProjectTimeline, type ProjectTimelineValue } from './ProjectTimeline'
import './ProjectDetails.scss'

type ProjectStatus = Project['status']

// Projekte gibt es nur in den drei festen Lebensbereichen (`LifeSpace`), wie im ProjectSwitcher.
const LIFE_SPACES: readonly LifeSpace[] = ['personal', 'work', 'home']
const STATUSES: readonly ProjectStatus[] = ['active', 'completed', 'archived']

export interface ProjectDetailsProps {
  project: Project
  /**
   * Nach dem Umzug in einen anderen Lebensbereich — und nach seinem Rückgängig. Die Seite geht
   * damit mit dem Projekt mit; sonst stünde man auf einer Seite, deren Projekt in der Seitenleiste fehlt.
   */
  onMovedToLifeSpace?: (lifeSpace: LifeSpace) => void
}

/**
 * **Das Projekt in den Aufgaben-Details** (#220) — was sie zeigen, wenn keine Aufgabe ausgewählt ist.
 * Gestalt „Steckbrief“ (Prototyp 29.09.2026): erst die Lage, dann die Werte. Oben Lebensbereich und
 * Status als Chips, darunter der **Zeitraum** als Strecke mit „heute“, darunter der **Fortschritt**.
 *
 * Alles schreibt sofort, wie überall in den Details. Name und Notizen bearbeitet der Projektkopf.
 *
 * @example
 * <ProjectDetails project={project} onMovedToLifeSpace={space => followProject(space)} />
 */
export function ProjectDetails({ project, onMovedToLifeSpace }: ProjectDetailsProps) {
  const { t } = useTranslation()
  const source = useDataSource()
  const progress = useProjectTaskProgress(project.id)
  const timeline = useMemo(
    () => ({ startDate: project.startDate, endDate: project.endDate }),
    [project.startDate, project.endDate],
  )

  const moveTo = useCallback(async (lifeSpace: LifeSpace) => {
    const move = await source.moveProjectToLifeSpace(project.id, lifeSpace)
    if (!move) return
    onMovedToLifeSpace?.(lifeSpace)
    const from = move.from.lifeSpace
    showUndoToast({
      message: t('projectDetails.moved', { space: t(`lifeSpaces.${lifeSpace}`) }),
      undoLabel: t('common.undo'),
      undoneMessage: t('projectDetails.moveUndone', { space: t(`lifeSpaces.${from}`) }),
      icon: <LifeSpaceColorIcon lifeSpace={lifeSpace} size={18} />,
      onUndo: async () => {
        await source.restoreProjectLifeSpace(move)
        onMovedToLifeSpace?.(from)
      },
    })
  }, [source, project.id, onMovedToLifeSpace, t])

  const setStatus = useCallback((status: ProjectStatus) => {
    if (status !== project.status) void source.updateProject(project.id, { status })
  }, [source, project.id, project.status])

  const setDates = useCallback((next: ProjectTimelineValue) => {
    void source.updateProject(project.id, next)
  }, [source, project.id])

  return (
    <div className="project-details">
      <div className="project-details__chips">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="project-details__chip" aria-label={t('projectDetails.lifeSpace')}>
              <LifeSpaceColorIcon lifeSpace={project.lifeSpace} size={16} />
              <span>{t(`lifeSpaces.${project.lifeSpace}`)}</span>
              <ChevronDown size={14} className="project-details__chevron" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {LIFE_SPACES.map(space => (
              <DropdownMenuItem key={space} className="project-details__menu-item" onSelect={() => { if (space !== project.lifeSpace) void moveTo(space) }}>
                <LifeSpaceColorIcon lifeSpace={space} size={16} />
                <span className="project-details__menu-label">{t(`lifeSpaces.${space}`)}</span>
                {space === project.lifeSpace && <Check size={16} className="context-menu-content__item-check" aria-hidden="true" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`project-details__chip project-details__chip--${project.status}`}
              aria-label={t('projectDetails.status.label')}
            >
              <StatusDot status={project.status} />
              <span>{t(`projectDetails.status.${project.status}`)}</span>
              <ChevronDown size={14} className="project-details__chevron" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {STATUSES.map(status => (
              <DropdownMenuItem key={status} className="project-details__menu-item" onSelect={() => setStatus(status)}>
                <StatusDot status={status} />
                <span className="project-details__menu-label">{t(`projectDetails.status.${status}`)}</span>
                {status === project.status && <Check size={16} className="context-menu-content__item-check" aria-hidden="true" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ProjectTimeline value={timeline} onValueChange={setDates} />

      <section className="project-details__card" aria-label={t('projectDetails.progress.title')}>
        <h3 className="project-details__card-title">{t('projectDetails.progress.title')}</h3>
        {progress.total > 0 ? (
          <>
            <p className="project-details__progress">
              <strong className="project-details__progress-count">{progress.done}</strong>
              {' '}{t('projectDetails.progress.of', { count: progress.total })}
            </p>
            <WaveProgress value={progress.value} className="project-details__wave project-details__wave--done" />
          </>
        ) : (
          <p className="project-details__caption">{t('projectDetails.progress.empty')}</p>
        )}
      </section>
    </div>
  )
}

function StatusDot({ status }: { status: ProjectStatus }) {
  return <span className={`project-details__dot project-details__dot--${status}`} aria-hidden="true" />
}
